import React, { useState, useEffect, useRef, useMemo } from 'react';
import { fehlertext } from '../lib/fehlertext';
import { useSearchParams } from 'react-router-dom';
import { settingsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import BackupRestore from '../components/BackupRestore';
import { SystemDiagnostics } from '../components/SystemDiagnostics';
import { ClientErrors } from '../components/ClientErrors';
import { NotificationSettings } from '../components/NotificationSettings';
import { AISettings } from '../components/AISettings';
import { AssistantLimitsSettings } from '../components/AssistantLimitsSettings';
import { AssistantChatLogs } from '../components/AssistantChatLogs';
import { AssistantUsageAnalytics } from '../components/AssistantUsageAnalytics';
import { ReminderSettings } from '../components/ReminderSettings';
import { SchoolHolidaysSettings } from '../components/SchoolHolidaysSettings';
import { LexwareSettings } from '../components/LexwareSettings';
import AdminUsers from './AdminUsers';
import {
  Mail,
  Save,
  Loader2,
  Info,
  Send,
  Image as ImageIcon,
  Upload,
  Trash2,
  Paintbrush,
  Shield,
  Building2,
  Plug,
  Sparkles,
  Server,
  CalendarDays,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  getVisibleSettingsTabs,
  canSeeSettingsTab,
  hasFullSettingsAccess,
} from '../lib/settingsAccess';

const TAB_ICONS = {
  verwaltung: Shield,
  email: Mail,
  organisation: Building2,
  schulferien: CalendarDays,
  integrationen: Plug,
  ki: Sparkles,
  system: Server,
};

const AdminSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwortHinterlegt, setPasswortHinterlegt] = useState(false);
  const [testing, setTesting] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const fileInputRef = useRef(null);
  const visibleTabs = useMemo(() => getVisibleSettingsTabs(user?.role), [user?.role]);
  const [activeTab, setActiveTab] = useState(() => getVisibleSettingsTabs(user?.role)[0]?.value || 'schulferien');
  const [techAck, setTechAck] = useState({});
  const [pendingTab, setPendingTab] = useState(null);
  const [ackChecked, setAckChecked] = useState(false);
  const TECH_TABS = ['integrationen', 'ki', 'system'];
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('focus') && canSeeSettingsTab(user?.role, 'verwaltung')) {
      setActiveTab('verwaltung');
    }
  }, [searchParams, user?.role]);
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: 587,
    username: '',
    password: '',
    sender_email: '',
    recipient_email: '',
    use_tls: true,
  });
  const [branding, setBranding] = useState({
    welcome_text: 'Willkommen zurück',
    logo_data: null,
    app_url: '',
  });

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const smtpPromise = hasFullSettingsAccess(user?.role)
        ? settingsAPI.getSMTP().catch(() => ({ data: {} }))
        : Promise.resolve({ data: {} });
      const [smtpRes, brandingRes] = await Promise.all([
        smtpPromise,
        settingsAPI.getBranding().catch(() => ({ data: {} })),
      ]);
      if (smtpRes.data && Object.keys(smtpRes.data).length > 0) {
        // Das Backend liefert '********', wenn ein Passwort hinterlegt ist. Das Feld wird
        // geleert, der Hinweis darüber merkt sich aber, dass eines gespeichert ist.
        setPasswortHinterlegt(smtpRes.data.password === '********');
        setSmtpConfig(prev => ({
          ...prev,
          ...smtpRes.data,
          password: '',
        }));
      }
      if (brandingRes.data) {
        setBranding({
          welcome_text: brandingRes.data.welcome_text || 'Willkommen zurück',
          logo_data: brandingRes.data.logo_data || null,
          app_url: brandingRes.data.app_url || '',
        });
      }
    } catch (error) {
      // Settings might not exist yet
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!smtpConfig.host || !smtpConfig.port || !smtpConfig.username || !smtpConfig.sender_email || !smtpConfig.recipient_email) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }

    try {
      setSaving(true);
      const daten = { ...smtpConfig };
      // Leeres Passwortfeld heißt "unverändert lassen" - also gar nicht mitsenden.
      if (!daten.password) delete daten.password;
      if (passwortHinterlegt) {
        // PUT verwirft weggelassene Felder von sich aus, schreibt aber ohne upsert:
        // deshalb nur, wenn schon eine Konfiguration existiert.
        await settingsAPI.updateSMTP(daten);
      } else {
        await settingsAPI.saveSMTP({ ...daten, password: smtpConfig.password || '' });
      }
      if (!smtpConfig.password && passwortHinterlegt) {
        toast.success('SMTP-Einstellungen gespeichert. Das gespeicherte Passwort bleibt erhalten.');
      } else {
        toast.success('SMTP-Einstellungen gespeichert');
        if (smtpConfig.password) setPasswortHinterlegt(true);
      }
    } catch (error) {
      toast.error(fehlertext(error, 'Fehler beim Speichern'));
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    try {
      setTesting(true);
      const response = await settingsAPI.testSMTP();
      toast.success(response.data.message || 'Test-E-Mail erfolgreich gesendet');
    } catch (error) {
      toast.error(fehlertext(error, 'Test-E-Mail konnte nicht gesendet werden'));
    } finally {
      setTesting(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Logo darf maximal 2 MB groß sein');
      return;
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Bitte eine Bilddatei auswählen');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setBranding(prev => ({ ...prev, logo_data: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = () => {
    setBranding(prev => ({ ...prev, logo_data: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSaveBranding = async () => {
    try {
      setSavingBranding(true);
      await settingsAPI.updateBranding({
        welcome_text: branding.welcome_text,
        logo_data: branding.logo_data,
        app_url: branding.app_url,
      });
      toast.success('Branding-Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Branding-Einstellungen');
    } finally {
      setSavingBranding(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 font-[Manrope]">
          Einstellungen
        </h1>
        <p className="text-slate-500 mt-1">
          {hasFullSettingsAccess(user?.role)
            ? 'Verwaltung, E-Mail, Organisation und mehr'
            : 'Schulferien für die Ferienzeiten-Erkennung pflegen'}
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(value) => {
          if (!canSeeSettingsTab(user?.role, value)) return;
          const isGf = user?.role === 'geschaeftsfuehrung';
          if (isGf && TECH_TABS.includes(value) && !techAck[value]) {
            setPendingTab(value);
            setAckChecked(false);
            return;
          }
          setActiveTab(value);
        }}
      >
        <TabsList className="flex flex-wrap h-auto gap-1 justify-start">
          {visibleTabs.map((t) => {
            const Icon = TAB_ICONS[t.value];
            return (
              <TabsTrigger key={t.value} value={t.value} data-testid={`tab-${t.value}`}>
                {Icon && <Icon className="w-4 h-4 mr-2" />}
                {t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* ── Verwaltung ── */}
        {canSeeSettingsTab(user?.role, 'verwaltung') && (
        <TabsContent value="verwaltung" className="space-y-6">
          <AdminUsers scope="verwaltung" embedded />
        </TabsContent>
        )}

        {/* ── E-Mail ── */}
        {canSeeSettingsTab(user?.role, 'email') && (
        <TabsContent value="email" className="space-y-6">
          {/* SMTP Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Mail className="h-5 w-5" />
                E-Mail Einstellungen (SMTP)
              </CardTitle>
              <CardDescription>
                Konfigurieren Sie den E-Mail-Server für den automatischen Versand der Stundenzettel.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <Alert className="border-blue-200 bg-blue-50">
                <Mail className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800">
                  <p className="font-medium mb-2">Wohin werden E-Mails versendet?</p>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    <li><strong>Stundenzettel-PDFs:</strong> Werden an die <em>Empfänger E-Mail</em> gesendet (z.B. verwaltung@firma.de)</li>
                    <li><strong>Passwort-Reset-Links:</strong> Werden direkt an die E-Mail des jeweiligen Mitarbeiters gesendet</li>
                  </ul>
                </AlertDescription>
              </Alert>
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 flex items-start gap-3">
                <Info className="h-5 w-5 text-slate-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-slate-700">
                  <p className="font-medium mb-1">So richten Sie SMTP ein:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-600">
                    <li>Gmail: smtp.gmail.com, Port 587, App-Passwort erstellen</li>
                    <li>Outlook: smtp-mail.outlook.com, Port 587</li>
                    <li>Eigener Server: Fragen Sie Ihren IT-Administrator</li>
                  </ul>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="host">SMTP Server *</Label>
                  <Input id="host" value={smtpConfig.host} onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })} placeholder="smtp.beispiel.de" data-testid="smtp-host-input" />
                </div>
                <div>
                  <Label htmlFor="port">Port *</Label>
                  <Input id="port" type="number" value={smtpConfig.port} onChange={(e) => setSmtpConfig({ ...smtpConfig, port: parseInt(e.target.value) || 587 })} placeholder="587" data-testid="smtp-port-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="username">Benutzername *</Label>
                  <Input id="username" value={smtpConfig.username} onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })} placeholder="user@beispiel.de" data-testid="smtp-username-input" />
                </div>
                <div>
                  <Label htmlFor="password">Passwort</Label>
                  <Input id="password" type="password" value={smtpConfig.password} onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })} placeholder="••••••••" data-testid="smtp-password-input" />
                  <p className="text-xs text-slate-500 mt-1" data-testid="smtp-password-hint">
                    {passwortHinterlegt
                      ? 'Ein Passwort ist gespeichert. Leer lassen, um es unverändert zu übernehmen.'
                      : 'Es ist noch kein Passwort gespeichert.'}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sender_email">Absender E-Mail *</Label>
                  <Input id="sender_email" type="email" value={smtpConfig.sender_email} onChange={(e) => setSmtpConfig({ ...smtpConfig, sender_email: e.target.value })} placeholder="noreply@firma.de" data-testid="smtp-sender-email-input" />
                </div>
                <div>
                  <Label htmlFor="recipient_email">Empfänger E-Mail *</Label>
                  <Input id="recipient_email" type="email" value={smtpConfig.recipient_email} onChange={(e) => setSmtpConfig({ ...smtpConfig, recipient_email: e.target.value })} placeholder="verwaltung@firma.de" data-testid="smtp-recipient-email-input" />
                  <p className="text-xs text-slate-500 mt-1">Alle Stundenzettel werden an diese Adresse gesendet</p>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base">TLS-Verschlüsselung verwenden</Label>
                  <p className="text-sm text-slate-500">Empfohlen für sichere Verbindungen</p>
                </div>
                <Switch checked={smtpConfig.use_tls} onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked })} data-testid="smtp-tls-switch" />
              </div>
              <div className="flex flex-col sm:flex-row justify-end gap-3">
                <Button variant="outline" onClick={handleTestEmail} disabled={testing || !smtpConfig.host || !smtpConfig.recipient_email} data-testid="test-smtp-button">
                  {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Test-E-Mail senden
                </Button>
                <Button className="bg-blue-800 hover:bg-blue-900" onClick={handleSave} disabled={saving} data-testid="save-smtp-button">
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Einstellungen speichern
                </Button>
              </div>
            </CardContent>
          </Card>
          <NotificationSettings />
          <ReminderSettings />
        </TabsContent>
        )}

        {/* ── Organisation ── */}
        {canSeeSettingsTab(user?.role, 'organisation') && (
        <TabsContent value="organisation" className="space-y-6">
          {/* Branding Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Paintbrush className="h-5 w-5" />
                Branding
              </CardTitle>
              <CardDescription>
                Passen Sie Logo und Willkommenstext für die Login-Seite und PDF-Exporte an.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label className="text-base mb-3 block">Firmenlogo</Label>
                <div className="flex items-start gap-6">
                  <div className="w-32 h-20 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center bg-slate-50 overflow-hidden flex-shrink-0">
                    {branding.logo_data ? (
                      <img src={branding.logo_data} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="branding-logo-preview" />
                    ) : (
                      <ImageIcon className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <div className="space-y-2">
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="branding-logo-file-input" />
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="branding-upload-logo-button">
                        <Upload className="h-4 w-4 mr-2" />
                        Logo hochladen
                      </Button>
                      {branding.logo_data && (
                        <Button variant="outline" size="sm" onClick={handleRemoveLogo} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="branding-remove-logo-button">
                          <Trash2 className="h-4 w-4 mr-2" />
                          Entfernen
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500">PNG, JPG oder SVG. Max 2 MB. Wird in PDFs und auf der Login-Seite angezeigt.</p>
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="welcome-text" className="text-base mb-2 block">Willkommenstext (Login-Seite)</Label>
                <Input id="welcome-text" value={branding.welcome_text} onChange={(e) => setBranding(prev => ({ ...prev, welcome_text: e.target.value }))} placeholder="Willkommen zurück" maxLength={100} data-testid="branding-welcome-text-input" />
                <p className="text-xs text-slate-500 mt-1">Wird als Überschrift auf der Login-Seite angezeigt.</p>
              </div>
              <div>
                <Label htmlFor="app-url" className="text-base mb-2 block">App-URL (für E-Mail-Links)</Label>
                <Input id="app-url" type="url" value={branding.app_url} onChange={(e) => setBranding(prev => ({ ...prev, app_url: e.target.value }))} placeholder="https://meine-domain.de" data-testid="branding-app-url-input" />
                <p className="text-xs text-slate-500 mt-1">
                  Diese URL wird in automatischen E-Mails (Passwort-Reset, Willkommens-E-Mail) verwendet. Ohne Eingabe wird die Standard-URL der Entwicklungsumgebung genutzt.
                </p>
              </div>
              <div className="flex justify-end">
                <Button className="bg-blue-800 hover:bg-blue-900" onClick={handleSaveBranding} disabled={savingBranding} data-testid="save-branding-button">
                  {savingBranding ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Branding speichern
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        )}

        {/* ── Schulferien ── */}
        {canSeeSettingsTab(user?.role, 'schulferien') && (
        <TabsContent value="schulferien" className="space-y-6">
          <div className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
            Aus diesen Zeiträumen erkennt der Stundenzettel automatisch die Ferientage. Gesetzliche
            Feiertage werden separat berechnet und müssen hier nicht eingetragen werden.
          </div>
          <SchoolHolidaysSettings />
        </TabsContent>
        )}

        {/* ── Integrationen ── */}
        {canSeeSettingsTab(user?.role, 'integrationen') && (
        <TabsContent value="integrationen" className="space-y-6">
          <LexwareSettings />
        </TabsContent>
        )}

        {/* ── KI ── */}
        {canSeeSettingsTab(user?.role, 'ki') && (
        <TabsContent value="ki" className="space-y-6">
          <AISettings />
          <AssistantLimitsSettings />
          <AssistantUsageAnalytics />
          <AssistantChatLogs />
        </TabsContent>
        )}

        {/* ── System ── */}
        {canSeeSettingsTab(user?.role, 'system') && (
        <TabsContent value="system" className="space-y-6">
          <BackupRestore />
          <SystemDiagnostics />
          <ClientErrors />
        </TabsContent>
        )}
      </Tabs>

      {/* Tech-Tab Bestätigungsfenster für GF */}
      <Dialog open={!!pendingTab} onOpenChange={(open) => { if (!open) setPendingTab(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Technischer Bereich — kurz bestätigen</DialogTitle>
            <DialogDescription>
              Dieser Bereich enthält technische Einstellungen. Änderungen hier können den Betrieb beeinflussen.
            </DialogDescription>
          </DialogHeader>

          <label className="flex items-start gap-2 py-2 cursor-pointer">
            <Checkbox checked={ackChecked} onCheckedChange={(v) => setAckChecked(!!v)} data-testid="tech-ack-checkbox" />
            <span className="text-sm">
              Ich weiß, was ich hier tue, und übernehme die Verantwortung für Änderungen in diesem Bereich.
            </span>
          </label>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingTab(null)} data-testid="tech-ack-cancel">
              Abbrechen
            </Button>
            <Button
              disabled={!ackChecked}
              data-testid="tech-ack-confirm"
              onClick={() => {
                setTechAck((prev) => ({ ...prev, [pendingTab]: true }));
                setActiveTab(pendingTab);
                setPendingTab(null);
              }}
            >
              Verstanden — fortfahren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminSettings;
